import { useCallback, useContext, useMemo } from 'react';
import { signupMessages } from './i18n';
import { configurationContext } from '../../../contexts/configuration';
import { ReconnectChat } from '../../reconnectChat/ReconnectChat';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
	EventType,
	IChatEventFormMessage,
	IChatMessagesOut,
	MessageType,
} from '@enuan/chat__utils';
import { Form } from '../../form';
import { useSendMessage } from '../../../hooks/core/messages.hooks';
import { messagesSelectors, messagesSlice } from '../../../reduxStore';
import { frameStateContext } from '../../../contexts/frame/context';
import { getFormLabels } from '../../../utils/getFormLabels';
import { getMessageFormPrefix } from '../../../utils/getMessageFormPrefix';
import { useLogError } from '../../ErrorBoundary/useLogError';
import { styled } from '@mui/material/styles';
import { FormFrame } from '../FormFrame';

export const FormScreen = styled(
	({ className }: { className?: string }) => {
		const { togglePage, formData } = useContext(frameStateContext);
		const { chat } = useContext(configurationContext);

		const logError = useLogError('FormScreen', { formData });

		const intl = useIntl();
		const header_title = intl.formatMessage(signupMessages.header_title);
		const header_description = intl.formatMessage(
			signupMessages.header_description,
		);
		const invalid_configuration = intl.formatMessage(
			signupMessages.invalid_configuration,
		);

		const header = useMemo(() => {
			const title = formData?.config?.header?.title ?? header_title;
			const description =
				formData?.config?.header?.description ?? header_description;
			const logo = formData?.config?.header?.logo ?? chat?.logo;
			const summary_title = formData?.config?.header?.summary_title;
			return { title, description, logo, summary_title };
		}, [formData, header_description, header_title, chat?.logo]);
		const { config } = formData ?? {};

		const prefix = formData?.message
			? getMessageFormPrefix(formData.message.client_id)
			: 'signup';

		const sendMessage = useSendMessage();
		const dispatch = useDispatch();

		const setReadOnly = useCallback(
			(readOnly: boolean) => {
				if (formData?.message && !formData.config?.reusable) {
					dispatch(
						messagesSlice.actions.toggleDisabled({
							client_id: formData.message.client_id,
							disabled: readOnly,
						}),
					);
				}
			},
			[dispatch, formData?.message, formData?.config?.reusable],
		);

		const onSubmitAction = useCallback(
			(values: Record<string, any> | undefined) => {
				const formLabels = getFormLabels(config?.items, values);

				const event: IChatMessagesOut<IChatEventFormMessage> = {
					_type: MessageType.EVENT,
					_data: {
						name: EventType.POSTBACK,
						parameters: {
							payload: values,
							metadata: {
								command_id: formData?.message?.command_id ?? '',
								labels: {
									form: formLabels,
									header,
								},
							},
						},
					},
				};

				setReadOnly(true);
				sendMessage(event)
					.then(() => togglePage('messages'))
					.catch((error) => {
						setReadOnly(false);
						logError('submit', { error, payload: { event } });
					});
				return true;
			},
			[
				formData?.message,
				config?.items,
				header,
				sendMessage,
				togglePage,
				setReadOnly,
				logError,
			],
		);

		// ! Used to wait message before to init Box
		// TODO Try to find another solution
		const message = useSelector((state) =>
			messagesSelectors.selectById(state, formData?.message?.client_id ?? ''),
		);
		if (!message && formData?.message?.client_id) return null;
		// ----------

		return (
			<FormFrame
				className={className}
				header={header}
				wave="right"
				body={
					<>
						{config && config.items ? (
							<Form
								prefix={prefix}
								onSubmitAction={onSubmitAction}
								formConfig={config}
							></Form>
						) : (
							invalid_configuration
						)}
					</>
				}
				footer={<ReconnectChat />}
			/>
		);
	},
	{ target: 'FormScreen-root', label: 'FormScreen' },
)({});
